import { Component, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { firstValueFrom, Subscription } from 'rxjs';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { SubrentalEntryDetailTableModel } from 'src/app/models/tables/subrental/subrental-entry-detail-table.model';
import { SubrentalEntryTableModel } from 'src/app/models/tables/subrental/subrental-entry-table.model';
import { HeaderService } from 'src/app/services/header/header.service';
import { SubrentalEntryDetailService } from 'src/app/services/subrental/subrental-entry-detail.service';
import { SubrentalEntryService } from 'src/app/services/subrental/subrental-entry.service';

@Component({
  selector: 'app-subrental-entry',
  templateUrl: './subrental-entry.component.html',
  styleUrls: ['./subrental-entry.component.scss'],
  standalone: false
})
export class SubrentalEntryComponent implements OnInit, OnDestroy {
  activeTabIndex = 0;
  ascendant: boolean = true;
  columnSort: string | null = null;
  detailData: PagedResponseModel<SubrentalEntryDetailTableModel> = new PagedResponseModel<SubrentalEntryDetailTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  filters: ColumnResponseModel[] = [];
  generalData: PagedResponseModel<SubrentalEntryTableModel> = new PagedResponseModel<SubrentalEntryTableModel>();
  generalHeader: ColumnResponseModel[] = [];
  limit: number = 10;
  listObservers: Subscription[] = [];
  nameWindow: string = 'Entradas de Subalquiler';
  page: number = 1;
  previousSort: { field: string; isAscsendant: number };
  projectName = ProjectName.projectName;
  subrentalEntryId: number = 0;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    },
  ];

  constructor(
    private router: Router,
    private title: Title,
    private headerService: HeaderService,
    private subrentalEntryService: SubrentalEntryService,
    private subrentalEntryDetailService: SubrentalEntryDetailService) {
  }

  async ngOnInit() {
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    const filters = localStorage.getItem('subrentalEntry');
    filters ? this.filters = JSON.parse(filters) : [];
    await Promise.all([
      this.getColumnsForGeneralConsultation(),
      this.getAllSubrentalEntryWithPagination()
    ]);
  }

  async ngOnDestroy() {
    this.listObservers.forEach(sub => sub.unsubscribe());
  }

  async exportDetailConsultation() {
    try {
      const blob = await firstValueFrom(this.subrentalEntryDetailService.exportDetailConsultation(this.filters, this.columnSort, this.ascendant));
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Entrada Subalquiler Detalle.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar las entradas de subalquiler detalle.', error);
    }
  }

  async exportGeneralConsultation() {
    try {
      const blob = await firstValueFrom(this.subrentalEntryService.exportGeneralConsultation(this.filters, this.columnSort, this.ascendant));
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Entradas Subalquiler.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar las entradas de subalquiler.', error);
    }
  }

  async getAllSubrentalEntryWithPagination() {
    try {
      this.generalData = await firstValueFrom(this.subrentalEntryService.getSubrentalEntriesForGeneralConsultation(this.page, this.limit, this.columnSort, this.ascendant, this.filters));
    } catch (error) {
      console.error('Error al buscar las entradas de subalquiler.', error);
    }
  }

  async getSubrentalEntryDetailsForDetailConsultation() {
    try {
      this.detailData = await firstValueFrom(this.subrentalEntryDetailService.getSubrentalEntryDetailsForDetailConsultation(this.page, this.limit, this.columnSort, this.ascendant, this.filters));
    } catch (error) {
      console.error('Error al buscar los detalles de las entradas de subalquiler.', error);
    }
  }

  async getColumnsForDetailConsultation() {
    try {
      if (this.detailHeader.length === 0) {
        this.detailHeader = await firstValueFrom(this.subrentalEntryDetailService.getColumnsForDetailConsultation());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta detalle.', error);
    }
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.generalHeader.length === 0) {
        this.generalHeader = await firstValueFrom(this.subrentalEntryService.getColumnsForGeneralConsultation());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta general.', error);
    }
  }

  public async handleTabChange(tabIndex: number) {
    this.generalHeader.length = this.generalHeader.length === 1 ? 0 : this.generalHeader.length;
    this.detailHeader.length = this.detailHeader.length === 1 ? 0 : this.detailHeader.length;

    if (this.activeTabIndex === tabIndex) return;

    this.page = 1;
    this.limit = 10;
    this.activeTabIndex = tabIndex;

    switch (this.activeTabIndex) {
      case 0:
        await Promise.all([
          this.getColumnsForGeneralConsultation(),
          this.getAllSubrentalEntryWithPagination()
        ]);
        this.generalData = {
          ...this.generalData,
          currentPage: 1,
          pageSize: 10
        };
        break;
      case 1:
        await Promise.all([
          this.getColumnsForDetailConsultation(),
          this.getSubrentalEntryDetailsForDetailConsultation()
        ]);
        this.detailData = {
          ...this.detailData,
          currentPage: 1,
          pageSize: 10
        };
        break;
    }
  }

  newSubrentalEntry() {
    this.router.navigate(['/subrental-entry/register']);
  }

  onAction(event: { action: string, item?: SubrentalEntryTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      this.router.navigate([`/subrental-entry/register/${event.item.id}`]);
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    switch (this.activeTabIndex) {
      case 0:
        await this.getAllSubrentalEntryWithPagination();
        break;
      case 1:
        await this.getSubrentalEntryDetailsForDetailConsultation();
        break;
    }
  }

  async onExport() {
    switch (this.activeTabIndex) {
      case 0:
        await this.exportGeneralConsultation();
        break;
      case 1:
        await this.exportDetailConsultation();
        break;
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);

    this.filters = this.removeDuplicates(filters);
    // Procesar cada filtro
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  private removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  private processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }
}
